import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { CelebrationService } from '../../core/services/celebration.service';
import { OcorrenciaSyncService } from '../../core/services/ocorrencia-sync.service';
import { Event, Celebration, CelebrationJob, CelebrationJobLocation, StaffLocationDetail, Staff, StaffEventTypeEnum } from '../../core/models/event.model';
import {
  AcaoOperador,
  Alocacao,
  ConfirmacaoAcaoDialogData,
  ConfirmacaoAcaoDialogResult,
  RegistroFila,
} from '../../core/models/alocacao.model';
import { ConfirmacaoAcaoDialogComponent } from './confirmacao-acao-dialog.component';
import {
  ACOES_OCORRENCIA,
  ACOES_SEM_MUDANCA_STATUS,
  isAcaoPermitida,
  normalizarStatus,
  staffParaRegistro,
} from './ocorrencia-acoes';

@Component({
  selector: 'app-staff-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
  ],
  templateUrl: './staff-manager.component.html',
  styleUrl: './staff-manager.component.scss'
})
export class StaffManagerComponent implements OnInit, OnDestroy {
  events: Event[] = [];
  selectedEvent: Event | null = null;
  selectedCelebration: Celebration | null = null;
  selectedJob: CelebrationJob | null = null;
  selectedLocation: CelebrationJobLocation | null = null;
  staffLocationDetail: StaffLocationDetail | null = null;

  filterStatus: string = 'all';
  loading = false;
  error: string | null = null;

  // Enum para usar no template
  StaffEventTypeEnum = StaffEventTypeEnum;

  // Registro de ocorrências em campo
  readonly ACOES = ACOES_OCORRENCIA;
  filaCount = 0;
  isSyncing = false;
  private filaSub?: Subscription;

  constructor(
    private celebrationService: CelebrationService,
    private readonly ocorrenciaSync: OcorrenciaSyncService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit() {
    this.loadOngoingEvents();
    this.filaSub = this.ocorrenciaSync.fila$.subscribe(fila => (this.filaCount = fila.length));
  }

  ngOnDestroy() {
    this.filaSub?.unsubscribe();
  }

  get isOnline(): boolean {
    return navigator.onLine;
  }

  /** Ao recuperar a conexão, tenta sincronizar a fila pendente automaticamente. */
  @HostListener('window:online')
  onConexaoRestaurada() {
    if (this.filaCount > 0 && !this.isSyncing) {
      this.sincronizar();
    }
  }

  // ---- Registro de ocorrências em campo ----

  isAcaoPermitida(acao: AcaoOperador, staff: Staff): boolean {
    return isAcaoPermitida(acao, normalizarStatus(staff.currentEvent.eventType.eventTypeEnum));
  }

  abrirConfirmacao(staff: Staff, acao: AcaoOperador): void {
    if (!this.selectedLocation) return;

    const membro: Alocacao = {
      celebrationJobLocationId: this.selectedLocation.locationUuid,
      localNome: this.selectedLocation.name,
      memberId: staff.member.id,
      membroNomeColete: staff.member.nomeColete,
      membroNome: staff.member.nomeCompleto || staff.member.nomeColete,
      telefoneCelular: staff.member.telefoneCelular,
      statusAtualEnum: normalizarStatus(staff.currentEvent.eventType.eventTypeEnum),
    };

    const ref = this.dialog.open<
      ConfirmacaoAcaoDialogComponent,
      ConfirmacaoAcaoDialogData,
      ConfirmacaoAcaoDialogResult
    >(ConfirmacaoAcaoDialogComponent, {
      data: { membro, acao },
      width: '420px',
      disableClose: true,
    });

    ref.afterClosed().subscribe(resultado => {
      if (!resultado) return; // usuário cancelou
      this.registrarAcao(staff, acao, resultado.observacao);
    });
  }

  registrarAcao(staff: Staff, acao: AcaoOperador, observacao: string): void {
    if (!this.selectedLocation) return;

    const registro: RegistroFila = staffParaRegistro(
      staff,
      this.selectedLocation.locationUuid,
      this.selectedLocation.name,
      acao,
      observacao,
    );

    this.ocorrenciaSync.registrar(registro).subscribe(res => {
      if (res.naoSuportado) {
        this.snackBar.open(
          `"${acao.label}" ainda não é suportado pelo servidor.`,
          'OK',
          { duration: 4000 },
        );
        return;
      }

      // Atualização otimista do status do card (apenas para ações sincronizáveis)
      if (!ACOES_SEM_MUDANCA_STATUS.has(acao.eventTypeEnum)) {
        staff.currentEvent.eventType.eventTypeEnum = acao.eventTypeEnum as StaffEventTypeEnum;
      }

      let msg: string;
      if (res.sincronizado) {
        msg = `"${acao.label}" enviado com sucesso.`;
      } else if (res.erro) {
        msg = `Sem conexão com o servidor. "${acao.label}" salvo na fila para sincronizar.`;
      } else {
        msg = `Offline. "${acao.label}" salvo na fila para sincronizar.`;
      }
      this.snackBar.open(msg, '', { duration: 2800 });
    });
  }

  sincronizar(): void {
    if (!this.isOnline) {
      this.snackBar.open('Sem conexão. Conecte-se à internet para sincronizar.', 'OK', { duration: 4000 });
      return;
    }
    if (this.filaCount === 0) {
      this.snackBar.open('Nenhum registro pendente na fila.', '', { duration: 2000 });
      return;
    }

    this.isSyncing = true;
    this.ocorrenciaSync.sincronizar().subscribe({
      next: res => {
        this.isSyncing = false;
        this.snackBar.open(res.message || 'Sincronização concluída!', 'OK', { duration: 3000 });
      },
      error: () => {
        this.isSyncing = false;
        this.snackBar.open('Erro ao sincronizar. Tente novamente.', 'OK', { duration: 4000 });
      },
    });
  }

  loadOngoingEvents() {
    this.loading = true;
    this.error = null;

    this.celebrationService.getOngoingCelebrations().subscribe({
      next: (events) => {
        this.events = events;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao carregar eventos';
        this.loading = false;
        console.error('Erro ao carregar eventos:', err);
      }
    });
  }

  selectEvent(event: Event) {
    this.selectedEvent = event;
    this.selectedCelebration = null;
    this.selectedJob = null;
    this.selectedLocation = null;
    this.staffLocationDetail = null;

    this.loading = true;
    this.celebrationService.getCelebrationById(event.uuid).subscribe({
      next: (celebration) => {
        this.selectedCelebration = celebration;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao carregar detalhes do evento';
        this.loading = false;
        console.error('Erro ao carregar celebração:', err);
      }
    });
  }

  selectJob(job: CelebrationJob) {
    this.selectedJob = job;
    this.selectedLocation = null;
    this.staffLocationDetail = null;
  }
  selectLocation(location: CelebrationJobLocation) {
    if (!this.selectedEvent) return;

    this.selectedLocation = location;
    this.loading = true;

    this.celebrationService.getLocationStaff(
      location.locationUuid
    ).subscribe({
      next: (staffDetail) => {
        this.staffLocationDetail = staffDetail;
        this.loading = false;
      },
      error: (err) => {
        this.error = 'Erro ao carregar staff da localização';
        this.loading = false;
        console.error('Erro ao carregar staff:', err);
      }
    });
  }

  goBack() {
    if (this.staffLocationDetail) {
      this.staffLocationDetail = null;
      this.selectedLocation = null;
    } else if (this.selectedLocation) {
      this.selectedLocation = null;
    } else if (this.selectedJob) {
      this.selectedJob = null;
    } else if (this.selectedCelebration) {
      this.selectedCelebration = null;
      this.selectedEvent = null;
    }
  }

  getFilteredStaff(): Staff[] {
    if (!this.staffLocationDetail) return [];

    if (this.filterStatus === 'all') {
      return this.staffLocationDetail.staffList;
    }

    return this.staffLocationDetail.staffList.filter(staff =>
      staff.currentEvent.eventType.eventTypeEnum === this.filterStatus
    );
  }

  getStaffStatusText(eventType: string): string {
    switch (eventType) {
      case StaffEventTypeEnum.START:
        return 'Iniciado';
      case StaffEventTypeEnum.SCHEDULED:
        return 'Agendado';
      case StaffEventTypeEnum.END_SUCCESS:
        return 'Concluído';
      default:
        return eventType;
    }
  }
  getStaffStatusClass(eventType: string): string {
    switch (eventType) {
      case StaffEventTypeEnum.START:
        return 'status-started';
      case StaffEventTypeEnum.SCHEDULED:
        return 'status-scheduled';
      case StaffEventTypeEnum.END_SUCCESS:
        return 'status-completed';
      default:
        return 'status-default';
    }
  }

  formatMinutesToHoursMinutes(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes}min`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}min`;
    }
  }

  getWhatsappLink(telefone: string | undefined | null): string {
    if (!telefone) return '';
    const numero = telefone.replace(/\D/g, '');
    // Considera válido apenas se tiver pelo menos 10 dígitos
    if (numero.length < 10) return '';
    return `https://wa.me/${numero}`;
  }
}
